import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Calendar, MapPin, Users, Ticket, Rocket, Globe, Mic, Trophy, Bot, Tent, PartyPopper, CheckCircle2, Search, AlertTriangle } from 'lucide-react';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { fetchEventosPublicos, inscribirseEvento, Evento as ApiEvento } from '../../services/eventoService';

const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Tipos de evento ──
type EventStatus = 'pasado' | 'próximo' | 'en curso';

interface EventItem {
    id: string;
    rawId?: number;
    status: EventStatus;
    date: string;
    title: string;
    category: string;
    description: string;
    details: string;
    capacity: string;
    location: string;
    tags: string[];
    accent: string;
    gradient: string;
    icon: React.ReactNode;
    image?: string;
    totalInscritos?: number;
    cuposDisponibles?: number;
    isRegistered?: boolean;
}

const DEFAULT_EVENT_IMAGES = [
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1000&q=80', // Bootcamp / Formación
    'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1000&q=80', // Summit / Conferencia
    'https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=1000&q=80', // Founders / Networking
    'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=1000&q=80', // Demo Day / Presentación
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80', // Workshop IA
    'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1000&q=80', // Feria / Expo
];

const mapApiEventoToEventItem = (api: ApiEvento): EventItem => {
    const rawStatus = (api.estado || '').toLowerCase();
    let status: EventStatus = 'próximo';
    if (rawStatus === 'en curso') status = 'en curso';
    else if (rawStatus.includes('pasado') || rawStatus === 'cancelado') status = 'pasado';

    const accent = api.color || colors.primary;
    const grad = api.gradiente || `linear-gradient(145deg, ${api.color_dark || colors.primaryDark}, ${accent})`;
    const eventImg = api.imagen || DEFAULT_EVENT_IMAGES[(api.id || 0) % DEFAULT_EVENT_IMAGES.length];

    return {
        id: String(api.id).padStart(2, '0'),
        rawId: api.id,
        status,
        date: api.fecha || 'Fecha por confirmar',
        title: api.titulo,
        category: api.categoria || 'General',
        description: api.descripcion,
        details: api.detalles || api.descripcion,
        capacity: `${api.capacidad} participantes`,
        location: api.ubicacion || 'Co Marca — Calacoto',
        tags: Array.isArray(api.tags) && api.tags.length > 0 ? api.tags : ['Co Marca', 'Comunidad'],
        accent,
        gradient: grad,
        icon: api.icono || <Mic size={32} />,
        image: eventImg,
        totalInscritos: api.total_inscritos,
        cuposDisponibles: api.cupos_disponibles,
        isRegistered: api.is_registered,
    };
};

const events: EventItem[] = [
    {
        id: '01',
        status: 'en curso',
        date: 'Mar — Jun 2026',
        title: 'Bootcamp: De la Idea al Negocio',
        category: 'Formación',
        description: 'Un programa intensivo de 12 semanas donde emprendedores bolivianos transforman una idea en un modelo de negocio viable, con mentoría personalizada, talleres prácticos y una Demo Day final ante inversores.',
        details: 'Cada cohorte trabaja en sprints semanales cubriendo validación de mercado, propuesta de valor, finanzas básicas, marketing digital y pitch. Al final, los participantes presentan ante un panel de inversores ángeles y representantes del ecosistema emprendedor boliviano.',
        capacity: '24 participantes',
        location: 'Co Marca — Calacoto',
        tags: ['12 semanas', '24 emprendedores', 'Demo Day final'],
        accent: colors.primary,
        gradient: `linear-gradient(145deg, ${colors.primaryDark}, ${colors.primary})`,
        icon: <Rocket size={32} />,
        image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1000&q=80',
    },
    {
        id: '02',
        status: 'próximo',
        date: '15 Abr 2026',
        title: 'Summit Bolivia Innova 2026',
        category: 'Conferencia',
        description: 'La conferencia de innovación y tecnología más importante de Bolivia reúne a líderes empresariales, startups, inversores y referentes del ecosistema digital latinoamericano en un día de charlas magistrales, paneles y networking de alto nivel.',
        details: 'Contaremos con speakers internacionales de Colombia, Argentina y México, además de los emprendedores bolivianos más destacados del año. La agenda incluye 3 tracks simultáneos: Tecnología & IA, Negocios & Escalabilidad, e Impacto Social. Cierre con coctel de networking exclusivo.',
        capacity: '200 asistentes',
        location: 'Sala Summit — Co Marca',
        tags: ['200 asistentes', 'Speakers internacionales', 'Networking VIP'],
        accent: colors.secondary,
        gradient: `linear-gradient(145deg, #451759, ${colors.secondary})`,
        icon: <Globe size={32} />,
        image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1000&q=80',
    },
    {
        id: '03',
        status: 'próximo',
        date: '28 Mar 2026',
        title: 'Noche de Founders: Pitch & Connect',
        category: 'Networking',
        description: 'Un evento mensual íntimo donde 6 fundadores presentan sus proyectos en formato pitch de 5 minutos ante una audiencia de pares, mentores e inversores. Más que una competencia — es una conversación honesta sobre el camino emprendedor.',
        details: 'El formato es deliberadamente informal: luces bajas, bebidas, música suave. Cada founder tiene 5 minutos de pitch y 10 minutos de preguntas abiertas. No hay ganadores ni perdedores — solo conversaciones que generan valor. Perfecto para founders en etapa temprana que buscan feedback real.',
        capacity: '60 asistentes',
        location: 'Co Marca — Espacio Principal',
        tags: ['6 pitches', 'Formato íntimo', 'Inversores presentes'],
        accent: colors.accentDark,
        gradient: `linear-gradient(145deg, #61257B, ${colors.accentDark})`,
        icon: <Mic size={32} />,
        image: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=1000&q=80',
    },
    {
        id: '04',
        status: 'pasado',
        date: 'Nov 2025',
        title: 'DemoDay Nexo Emprendedor — Cohorte 4',
        category: 'Demo Day',
        description: 'La cuarta cohorte del Programa Nexo Emprendedor presentó sus proyectos ante más de 80 inversores, empresarios y líderes del ecosistema. 8 startups, 12 semanas de trabajo intensivo, y un resultado que superó todas las expectativas.',
        details: 'De las 8 startups que presentaron, 5 recibieron ofertas de inversión esa misma noche. El proyecto ganador — una plataforma de telemedicina rural — levantó USD 45,000 en su ronda pre-semilla. Fue el Demo Day más exitoso en la historia de Co Marca hasta la fecha.',
        capacity: '80 asistentes',
        location: 'Sala Summit — Co Marca',
        tags: ['8 startups', 'USD 45K levantados', '80 asistentes'],
        accent: colors.secondaryLight,
        gradient: `linear-gradient(145deg, #2a0f3d, ${colors.secondaryLight})`,
        icon: <Trophy size={32} />,
        image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=1000&q=80',
    },
    {
        id: '05',
        status: 'pasado',
        date: 'Sep 2025',
        title: 'Workshop: Inteligencia Artificial para tu Negocio',
        category: 'Workshop',
        description: 'Un taller práctico de 8 horas donde profesionales y empresarios aprendieron a integrar herramientas de IA en sus flujos de trabajo reales — sin necesidad de saber programar. Sold out en menos de 48 horas desde su anuncio.',
        details: 'Los participantes trabajaron con ChatGPT, Midjourney, Make.com y herramientas de automatización para construir flujos de trabajo que ahorraban entre 5 y 15 horas semanales en sus negocios. El 92% calificó el taller como "inmediatamente aplicable" en su evaluación post-evento.',
        capacity: '30 participantes',
        location: 'Co Marca — Sala de Capacitación',
        tags: ['Sold out en 48h', '92% aplicabilidad', '8 horas intensivas'],
        accent: colors.primary,
        gradient: `linear-gradient(145deg, ${colors.primaryDark}, #C1348B)`,
        icon: <Bot size={32} />,
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80',
    },
    {
        id: '06',
        status: 'pasado',
        date: 'Jun 2025',
        title: 'Feria de Emprendimiento Bolivia 2025',
        category: 'Feria',
        description: 'Co Marca fue sede y organizador co-principal de la feria de emprendimiento más grande de La Paz, reuniendo a 45 expositores, 3 días de actividades, talleres gratuitos y más de 1,200 visitantes que exploraron el ecosistema emprendedor boliviano.',
        details: 'El evento contó con zona de exposición, escenario principal para charlas abiertas, área de coworking temporal gratuita, mentorías express de 15 minutos, y una zona especial para proyectos de impacto social. Fue transmitido en vivo con más de 8,000 vistas en redes sociales.',
        capacity: '1,200+ visitantes',
        location: 'Co Marca + Calacoto Open Air',
        tags: ['45 expositores', '3 días', '1,200+ visitantes'],
        accent: colors.accentDark,
        gradient: `linear-gradient(145deg, #451759, ${colors.accentDark})`,
        icon: <Tent size={32} />,
        image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1000&q=80',
    },
];

// ── Badge de estado ──
const StatusBadge = ({ status }: { status: EventStatus }) => {
    const { isDark } = useTheme();
    const config = {
        'en curso': {
            label: 'En curso',
            bg: isDark ? 'rgba(255, 217, 124, 0.15)' : 'rgba(97, 37, 123, 0.12)',
            color: isDark ? '#FFD97C' : '#61257B',
            border: isDark ? 'rgba(255, 217, 124, 0.35)' : 'rgba(97, 37, 123, 0.3)',
            dot: isDark ? '#FFD97C' : '#61257B',
        },
        'próximo': {
            label: 'Próximo',
            bg: isDark ? 'rgba(193, 52, 139, 0.18)' : 'rgba(193, 52, 139, 0.12)',
            color: isDark ? '#f472b6' : '#C1348B',
            border: isDark ? 'rgba(193, 52, 139, 0.35)' : 'rgba(193, 52, 139, 0.3)',
            dot: '#C1348B',
        },
        'pasado': {
            label: 'Realizado',
            bg: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
            color: isDark ? '#94a3b8' : '#64748b',
            border: isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0',
            dot: '#94a3b8',
        },
    };
    const c = config[status];
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: c.bg, border: `1.5px solid ${c.border}`,
            borderRadius: '100px', padding: '4px 12px',
            fontSize: '0.72rem', fontWeight: '700', color: c.color,
            letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
            <span style={{
                width: '6px', height: '6px', borderRadius: '50%', background: c.dot,
                boxShadow: status === 'en curso' ? `0 0 0 3px ${colors.secondaryLight}30` : 'none',
                animation: status === 'en curso' ? 'pulseDot 1.8s infinite' : 'none',
                display: 'inline-block',
            }} />
            {c.label}
        </span>
    );
};

// ── Event Card ──
function EventCard({
    event,
    index,
    onRegister,
    onSummary,
}: {
    event: EventItem;
    index: number;
    onRegister?: (event: EventItem) => void;
    onSummary?: (event: EventItem) => void;
}) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.08);
    const [hovered, setHovered] = useState(false);
    const isEven = index % 2 === 0;

    const imgBlock = (
        <div className="event-img-side" style={{
            flex: '0 0 420px', minHeight: '360px',
            position: 'relative', overflow: 'hidden',
            background: event.gradient,
        }}>
            {/* Fotografía de ejemplo en alta definición */}
            {event.image && (
                <img
                    src={event.image}
                    alt={event.title}
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.65s cubic-bezier(.16,1,.3,1)',
                        transform: hovered ? 'scale(1.08)' : 'scale(1)',
                        zIndex: 1,
                    }}
                />
            )}

            {/* Gradiente cinemático oscuro sobre la imagen para alto contraste y legibilidad */}
            <div style={{
                position: 'absolute', inset: 0,
                background: event.image
                    ? 'linear-gradient(to top, rgba(11,6,20,0.92) 0%, rgba(11,6,20,0.45) 50%, rgba(11,6,20,0.2) 100%)'
                    : event.gradient,
                zIndex: 2,
            }} />

            {/* Elementos informativos sobre la foto */}
            <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: '16px',
                zIndex: 3,
                padding: '24px',
            }}>
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', bottom: '-50px', left: '-30px', width: '220px', height: '220px', background: 'rgba(255,255,255,0.03)', borderRadius: '50%' }} />

                {/* Número decorativo */}
                <div style={{
                    position: 'absolute', top: '16px',
                    left: isEven ? 'auto' : '20px', right: isEven ? '20px' : 'auto',
                    fontSize: '4.5rem', fontWeight: '900',
                    color: 'rgba(255,255,255,0.22)', lineHeight: 1, letterSpacing: '-0.04em',
                }}>
                    {event.id}
                </div>

                <div style={{ fontSize: '3.6rem', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.6))' }}>{event.icon}</div>

                <div style={{
                    background: 'rgba(255,255,255,0.16)', backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.25)', borderRadius: '16px',
                    padding: '12px 24px', textAlign: 'center',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                }}>
                    <div style={{ fontSize: '1rem', fontWeight: '800', color: '#fff', lineHeight: 1 }}>{event.capacity}</div>
                    <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.92)', fontWeight: '500', marginTop: '4px' }}>{event.location}</div>
                </div>
            </div>

            {/* Fecha badge */}
            <div style={{
                position: 'absolute', bottom: '20px',
                left: isEven ? '20px' : 'auto', right: isEven ? 'auto' : '20px',
                background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px', padding: '6px 14px',
                fontSize: '0.75rem', fontWeight: '700',
                color: '#fff', letterSpacing: '0.04em',
                zIndex: 4,
            }}>
                {event.date}
            </div>

            {/* Accent bar */}
            <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
                background: `linear-gradient(90deg, ${event.accent}, ${event.accent}88)`,
                opacity: hovered ? 1 : 0.5, transition: 'opacity 0.3s',
                zIndex: 5,
            }} />
        </div>
    );

    const textBlock = (
        <div className="event-content-side" style={{
            flex: 1, minWidth: '280px', padding: '44px 48px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
            {/* Status + categoría */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <StatusBadge status={event.status} />
                <span style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : event.accent }}>
                    {event.category}
                </span>
                <div style={{ height: '2px', width: '24px', borderRadius: '2px', background: isDark ? '#FFD97C' : event.accent }} />
            </div>
            {/* Título */}
            <h2 style={{
                fontSize: 'clamp(1.3rem, 2.4vw, 1.9rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a',
                lineHeight: '1.2', letterSpacing: '-0.02em', marginBottom: '14px',
                fontFamily: "'DM Sans', sans-serif",
            }}>
                {event.title}
            </h2>
            {/* Descripción corta */}
            <p style={{ fontSize: '0.97rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85', marginBottom: '14px' }}>
                {event.description}
            </p>
            {/* Detalle */}
            <p style={{ fontSize: '0.88rem', color: isDark ? '#94a3b8' : '#64748b', lineHeight: '1.8', marginBottom: '24px' }}>
                {event.details}
            </p>
            {/* Tags */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
                {event.tags.map(tag => (
                    <span key={tag} style={{
                        background: isDark ? 'rgba(255,255,255,0.06)' : `${event.accent}12`,
                        border: isDark ? '1px solid rgba(255,255,255,0.14)' : `1.5px solid ${event.accent}28`,
                        borderRadius: '100px', padding: '5px 14px',
                        fontSize: '0.78rem', fontWeight: '600',
                        color: isDark ? '#FFD97C' : event.accent,
                        letterSpacing: '0.02em',
                    }}>
                        {tag}
                    </span>
                ))}
            </div>
            {/* CTA condicional */}
            {event.status !== 'pasado' ? (
                <div>
                    <button
                        type="button"
                        onClick={() => { if (!event.isRegistered && onRegister) onRegister(event); }}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: event.isRegistered ? (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0') : 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                            color: event.isRegistered ? (isDark ? '#94a3b8' : '#64748b') : '#fff',
                            border: 'none',
                            borderRadius: '12px', padding: '11px 24px',
                            fontWeight: '700', fontSize: '0.88rem',
                            boxShadow: event.isRegistered ? 'none' : '0 4px 16px rgba(97,37,123,0.35)',
                            transition: 'all 0.2s', cursor: event.isRegistered ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={e => { if (!event.isRegistered) e.currentTarget.style.transform = 'translateX(4px)'; }}
                        onMouseLeave={e => { if (!event.isRegistered) e.currentTarget.style.transform = 'translateX(0)'; }}
                        disabled={event.isRegistered}
                    >
                        {event.isRegistered ? <><CheckCircle2 size={16} /> Ya estás inscrito</> : (event.status === 'en curso' ? 'Inscribirme ahora →' : 'Reservar mi lugar →')}
                    </button>
                </div>
            ) : (
                <div>
                    <button
                        type="button"
                        onClick={() => onSummary && onSummary(event)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                            color: isDark ? '#FFD97C' : '#61257B',
                            border: isDark ? '1.5px solid rgba(255,217,124,0.3)' : '1.5px solid rgba(97,37,123,0.2)',
                            borderRadius: '12px', padding: '10px 22px',
                            fontWeight: '700', fontSize: '0.86rem',
                            transition: 'all 0.2s', cursor: 'pointer',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        Ver resumen y resultados ↗
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView
                    ? (hovered ? 'translateY(-4px)' : 'translateY(0)')
                    : (isEven ? 'translateX(-40px)' : 'translateX(40px)'),
                opacity: inView ? (event.status === 'pasado' ? 0.85 : 1) : 0,
                transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.06}s`,
                width: '100%',
                filter: event.status === 'pasado' ? 'saturate(0.8)' : 'none',
            }}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <article
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="event-card-inner"
                    style={{
                        display: 'flex',
                        flexDirection: isEven ? 'row' : 'row-reverse',
                        borderRadius: '28px', overflow: 'hidden',
                        background: isDark ? '#150d24' : (event.status === 'pasado' ? '#fafafa' : '#fff'),
                        border: isDark ? '1px solid rgba(193,52,139,0.25)' : (hovered ? `1.5px solid ${event.accent}35` : '1.5px solid #f1f5f9'),
                        boxShadow: hovered
                            ? `0 24px 64px ${event.accent}18, 0 8px 32px rgba(0,0,0,0.07)`
                            : '0 4px 24px rgba(0,0,0,0.05)',
                        transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                        flexWrap: 'wrap',
                        width: '100%',
                    }}
                >
                    {isEven ? <>{imgBlock}{textBlock}</> : <>{textBlock}{imgBlock}</>}
                </article>
            </BorderGlow>
        </div>
    );
}

// ── Hero ──
function HeroSection({
    activeFilter,
    onFilterChange,
    searchQuery,
    onSearchChange,
}: {
    activeFilter: string;
    onFilterChange: (filter: string) => void;
    searchQuery: string;
    onSearchChange: (q: string) => void;
}) {
    const { isDark } = useTheme();
    const [headerRef, headerInView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => { const t = setTimeout(() => setForced(true), 100); return () => clearTimeout(t); }, []);
    const visible = headerInView || forced;

    const filterOptions = [
        { id: 'todos', label: 'Todos los eventos' },
        { id: 'en curso', label: '● En curso' },
        { id: 'próximo', label: '◆ Próximos' },
        { id: 'pasado', label: '✓ Realizados' },
    ];

    return (
        <section style={{
            background: isDark
                ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                : 'linear-gradient(135deg, #fff 0%, #faf5fc 55%, #f5e8fa 100%)',
            padding: 'clamp(100px, 14vw, 140px) 32px clamp(60px, 8vw, 80px)',
            position: 'relative', overflow: 'hidden',
        }}>
            <div className="blob-anim" style={{
                position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px',
                background: isDark
                    ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))'
                    : 'linear-gradient(135deg, rgba(97,37,123,0.14), rgba(193,52,139,0.08))',
                pointerEvents: 'none',
            }} />
            {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
            <HexagonCornerDecoration
                variant="horizontal-degrade"
                direction="left-to-right"
                opacity={isDark ? 0.70 : 0.50}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <div ref={headerRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                <h1 className={`fade-up-evt d1 ${visible ? 'in' : ''}`} style={{
                    fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                    fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                    lineHeight: '1.08', letterSpacing: '-0.025em', marginBottom: '12px',
                }}>
                    Nuestros <span style={{
                        background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>Eventos</span>
                </h1>
                <h2 className={`fade-up-evt d1 ${visible ? 'in' : ''}`} style={{
                    fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                    fontWeight: '600', color: isDark ? '#cbd5e1' : '#475569',
                    letterSpacing: '-0.01em', marginBottom: '20px',
                }}>
                    Donde el conocimiento se <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>convierte en acción</span>
                </h2>
                <p className={`fade-up-evt d2 ${visible ? 'in' : ''}`} style={{
                    fontSize: '1.05rem', color: isDark ? '#94a3b8' : '#64748b',
                    maxWidth: '580px', margin: '0 auto', lineHeight: '1.75', textAlign: 'justify',
                }}>
                    Desde bootcamps intensivos hasta conferencias de impacto regional — Co Marca es el epicentro donde emprendedores, líderes y visionarios bolivianos se encuentran, aprenden y construyen juntos.
                </p>

                {/* Filtro de estado interactivo */}
                <div className={`fade-up-evt d2 ${visible ? 'in' : ''}`} style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '28px', flexWrap: 'wrap' }}>
                    {filterOptions.map(f => {
                        const active = activeFilter === f.id;
                        return (
                            <button
                                key={f.id}
                                onClick={() => onFilterChange(f.id)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    background: active
                                        ? 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)'
                                        : (isDark ? 'rgba(255,255,255,0.06)' : '#fff'),
                                    border: active
                                        ? '1.5px solid transparent'
                                        : (isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #e2e8f0'),
                                    borderRadius: '100px', padding: '8px 20px',
                                    fontSize: '0.82rem', fontWeight: '700',
                                    color: active ? '#ffffff' : (isDark ? '#e2e8f0' : '#475569'),
                                    cursor: 'pointer', transition: 'all 0.25s',
                                    boxShadow: active ? '0 6px 20px rgba(97,37,123,0.35)' : 'none',
                                    transform: active ? 'translateY(-2px)' : 'translateY(0)',
                                }}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                {/* Search input */}
                <div className={`fade-up-evt d2 ${visible ? 'in' : ''}`} style={{ maxWidth: '440px', margin: '20px auto 0', position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Buscar evento por nombre, tema o ciudad..."
                        value={searchQuery}
                        onChange={e => onSearchChange(e.target.value)}
                        style={{
                            width: '100%', padding: '12px 20px 12px 42px', borderRadius: '100px',
                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                            background: isDark ? '#150d24' : '#ffffff',
                            color: isDark ? '#ffffff' : '#0f172a', fontSize: '0.9rem',
                            outline: 'none', fontFamily: "'DM Sans', sans-serif",
                            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                        }}
                    />
                    <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', opacity: 0.6 }}><Search size={16} /></span>
                    {searchQuery && (
                        <button
                            onClick={() => onSearchChange('')}
                            style={{
                                position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold',
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Stats */}
                <div className={`fade-up-evt d3 ${visible ? 'in' : ''}`} style={{ display: 'flex', justifyContent: 'center', gap: '0', marginTop: '40px', flexWrap: 'wrap' }}>
                    {[
                        { value: '60+', label: 'Eventos realizados', color: isDark ? '#FFD97C' : '#61257B' },
                        { value: '5,000+', label: 'Asistentes totales', color: isDark ? '#C1348B' : '#C1348B' },
                        { value: '4.8★', label: 'Calificación promedio', color: isDark ? '#FFD97C' : '#EA8406' },
                    ].map((stat, i) => (
                        <div key={stat.label} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            padding: '20px 36px',
                            borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.25)' : '1.5px solid #f1f5f9') : 'none',
                        }}>
                            <div style={{ fontSize: '2.6rem', fontWeight: '900', color: stat.color, lineHeight: 1, letterSpacing: '-0.03em' }}>{stat.value}</div>
                            <div style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '6px' }}>{stat.label}</div>
                        </div>
                    ))}
                </div>
                <div className={`fade-up-evt d3 ${visible ? 'in' : ''}`} style={{ marginTop: '32px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes
                        variant="scroll-prompt"
                        size="md"
                        promptText="Explora nuestros eventos"
                    />
                </div>
            </div>
        </section>
    );
}

// ── Event RSVP Registration Modal ──
function EventRegistrationModal({
    event,
    onClose,
    onSuccess,
}: {
    event: EventItem;
    onClose: () => void;
    onSuccess?: () => void;
}) {
    const { isDark } = useTheme();
    const { user } = useAuth();
    const [nombre, setNombre] = useState(user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : '');
    const [email, setEmail] = useState(user?.email || '');
    const [telefono, setTelefono] = useState(user?.telefono || '');
    const [empresa, setEmpresa] = useState(user?.cargo || '');
    const [cupos, setCupos] = useState('1');
    const [confirmed, setConfirmed] = useState(false);
    const [ticketId, setTicketId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg('');
        setSubmitting(true);
        try {
            if (event.rawId) {
                const res = await inscribirseEvento(event.rawId, {
                    nombre_completo: nombre,
                    email,
                    telefono,
                    empresa_o_cargo: empresa,
                    cupos: parseInt(cupos, 10) || 1,
                });
                setTicketId(res.codigo_pase);
                setConfirmed(true);
                if (onSuccess) onSuccess();
            } else {
                const code = `CM-EVT-${Math.floor(1000 + Math.random() * 9000)}`;
                setTicketId(code);
                setConfirmed(true);
            }
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            const msg = err.response?.data?.non_field_errors?.[0] ||
                err.response?.data?.detail ||
                err.response?.data?.error ||
                'Ocurrió un error al procesar la inscripción. Verifica los cupos e inténtalo de nuevo.';
            setErrorMsg(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '26px', maxWidth: '560px', width: '100%',
                    border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                    overflow: 'hidden', position: 'relative',
                    fontFamily: "'DM Sans', sans-serif",
                }}
            >
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #420f54 0%, #61257B 50%, #C1348B 100%)',
                    padding: '26px 28px', color: '#fff', position: 'relative',
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute', top: '16px', right: '16px',
                            background: 'rgba(255,255,255,0.2)', border: 'none',
                            color: '#fff', width: '32px', height: '32px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '1rem',
                        }}
                    >
                        ✕
                    </button>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        background: 'rgba(255,255,255,0.18)', borderRadius: '100px',
                        padding: '3px 12px', fontSize: '0.7rem', fontWeight: '800',
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px',
                    }}>
                        Inscripción Oficial
                    </div>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: '800', lineHeight: '1.25', margin: 0 }}>
                        {event.title}
                    </h3>
                    <div style={{ fontSize: '0.82rem', opacity: 0.9, marginTop: '6px' }}>
                        <span style={{display:'inline-flex', alignItems:'center', gap:'4px'}}><Calendar size={14}/> {event.date}</span> · <span style={{display:'inline-flex', alignItems:'center', gap:'4px'}}><MapPin size={14}/> {event.location}</span>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '26px 28px' }}>
                    {confirmed ? (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                            <div style={{ marginBottom: '8px' }}><PartyPopper size={48} /></div>
                            <h4 style={{ fontSize: '1.4rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '6px' }}>
                                ¡Inscripción Confirmada!
                            </h4>
                            <p style={{ fontSize: '0.9rem', color: isDark ? '#cbd5e1' : '#64748b', marginBottom: '20px' }}>
                                Hemos registrado tu pase para <strong>{event.title}</strong>. Te enviamos una copia a tu correo electrónico.
                            </p>

                            {/* Digital Ticket Pass */}
                            <div style={{
                                background: isDark ? '#1b102e' : '#faf5fc',
                                border: isDark ? '1.5px dashed rgba(193,52,139,0.5)' : '1.5px dashed #d8b4fe',
                                borderRadius: '18px', padding: '18px', marginBottom: '22px', textAlign: 'left',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '0.72rem', fontWeight: '800', color: isDark ? '#FFD97C' : '#61257B', textTransform: 'uppercase' }}>
                                        PASE DIGITAL OFICIAL
                                    </span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '900', color: '#C1348B', fontFamily: 'monospace' }}>
                                        {ticketId}
                                    </span>
                                </div>
                                <div style={{ fontSize: '0.95rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a' }}>
                                    {nombre} ({cupos} {cupos === '1' ? 'pase' : 'pases'})
                                </div>
                                <div style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#64748b', marginTop: '4px' }}>
                                    {email} · {telefono}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                <a
                                    href={googleCalendarUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                        color: '#fff', textDecoration: 'none', borderRadius: '12px',
                                        padding: '11px 20px', fontSize: '0.85rem', fontWeight: '700',
                                    }}
                                >
                                    <span style={{display:'inline-flex', alignItems:'center', gap:'6px'}}><Calendar size={16}/> Añadir a Google Calendar</span>
                                </a>
                                <button
                                    onClick={onClose}
                                    style={{
                                        background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                                        color: isDark ? '#ffffff' : '#475569', border: 'none',
                                        borderRadius: '12px', padding: '11px 20px', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer',
                                    }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                    Nombre completo *
                                </label>
                                <input
                                    type="text" required placeholder="Tu nombre"
                                    value={nombre} onChange={e => setNombre(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px',
                                        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                        background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                        fontSize: '0.9rem', outline: 'none',
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                        Correo electrónico *
                                    </label>
                                    <input
                                        type="email" required placeholder="tu@email.com"
                                        value={email} onChange={e => setEmail(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                            background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                            fontSize: '0.9rem', outline: 'none',
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                        WhatsApp *
                                    </label>
                                    <input
                                        type="tel" required placeholder="+591 7XX XXXXX"
                                        value={telefono} onChange={e => setTelefono(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                            background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                            fontSize: '0.9rem', outline: 'none',
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 2 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                        Empresa o Cargo (opcional)
                                    </label>
                                    <input
                                        type="text" placeholder="Mi Startup / Empresa S.A."
                                        value={empresa} onChange={e => setEmpresa(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                            background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                            fontSize: '0.9rem', outline: 'none',
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                        Pases
                                    </label>
                                    <select
                                        value={cupos} onChange={e => setCupos(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 12px', borderRadius: '10px',
                                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                            background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                            fontSize: '0.9rem', outline: 'none',
                                        }}
                                    >
                                        <option value="1">1 persona</option>
                                        <option value="2">2 personas</option>
                                        <option value="3">3 personas</option>
                                        <option value="4">4 personas</option>
                                    </select>
                                </div>
                            </div>

                            {errorMsg && (
                                <div style={{
                                    padding: '10px 14px', borderRadius: '10px',
                                    background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)',
                                    color: '#f87171', fontSize: '0.82rem', fontWeight: '600',
                                }}>
                                    <span style={{display:'inline-flex', alignItems:'center', gap:'6px'}}><AlertTriangle size={16}/> {errorMsg}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                style={{
                                    marginTop: '8px', padding: '14px', borderRadius: '12px',
                                    border: 'none', background: submitting ? '#64748b' : 'linear-gradient(135deg, #61257B, #C1348B)',
                                    color: '#fff', fontWeight: '800', fontSize: '0.95rem',
                                    cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: '0 8px 24px rgba(97,37,123,0.35)',
                                }}
                            >
                                {submitting ? 'Procesando registro...' : 'Confirmar Inscripción Inmediata →'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Event Summary Modal ──
function EventSummaryModal({
    event,
    onClose,
}: {
    event: EventItem;
    onClose: () => void;
}) {
    const { isDark } = useTheme();

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '26px', maxWidth: '620px', width: '100%',
                    border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                    overflow: 'hidden', position: 'relative',
                    fontFamily: "'DM Sans', sans-serif",
                }}
            >
                <div style={{
                    background: event.gradient, padding: '28px', color: '#fff', position: 'relative',
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute', top: '16px', right: '16px',
                            background: 'rgba(0,0,0,0.4)', border: 'none',
                            color: '#fff', width: '32px', height: '32px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '1rem',
                        }}
                    >
                        ✕
                    </button>
                    <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#FFD97C', textTransform: 'uppercase', marginBottom: '8px' }}>
                        ✓ Evento Realizado con Éxito
                    </div>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: '800', lineHeight: '1.2' }}>
                        {event.title}
                    </h3>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '6px' }}>
                        <span style={{display:'inline-flex', alignItems:'center', gap:'4px'}}><Calendar size={14}/> {event.date}</span> · <span style={{display:'inline-flex', alignItems:'center', gap:'4px'}}><Users size={14}/> {event.capacity}</span>
                    </div>
                </div>

                <div style={{ padding: '28px' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '10px' }}>
                        Impacto y Resultados Clave
                    </h4>
                    <p style={{ fontSize: '0.92rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.75', marginBottom: '20px' }}>
                        {event.details}
                    </p>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
                        {event.tags.map(t => (
                            <span key={t} style={{
                                background: isDark ? 'rgba(193,52,139,0.2)' : 'rgba(97,37,123,0.1)',
                                border: isDark ? '1px solid rgba(193,52,139,0.3)' : '1px solid rgba(97,37,123,0.2)',
                                borderRadius: '100px', padding: '4px 12px', fontSize: '0.75rem',
                                fontWeight: '700', color: isDark ? '#FFD97C' : '#61257B',
                            }}>
                                ✓ {t}
                            </span>
                        ))}
                    </div>

                    <button
                        onClick={onClose}
                        style={{
                            width: '100%', padding: '12px', borderRadius: '12px',
                            background: 'linear-gradient(135deg, #61257B, #C1348B)',
                            color: '#fff', border: 'none', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
                        }}
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Section dividers ──
function SectionLabel({ label, color }: { label: string; color: string }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.2);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(.16,1,.3,1)',
            marginBottom: '28px',
        }}>
            <div style={{ height: '2px', flex: 1, background: isDark ? 'linear-gradient(to right, rgba(193,52,139,0.4), transparent)' : `linear-gradient(to right, ${color}40, transparent)`, borderRadius: '2px' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : color, whiteSpace: 'nowrap' }}>
                {label}
            </span>
            <div style={{ height: '2px', flex: 1, background: isDark ? 'linear-gradient(to left, rgba(193,52,139,0.4), transparent)' : `linear-gradient(to left, ${color}40, transparent)`, borderRadius: '2px' }} />
        </div>
    );
}

// ── CTA Banner ──
function CtaBanner() {
    const [ref, inView] = useInView(0.2);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} style={{
            marginTop: '64px', borderRadius: '28px',
            background: 'linear-gradient(135deg, #420f54 0%, #61257B 50%, #C1348B 100%)',
            padding: '56px 48px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '28px',
            opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.75s cubic-bezier(.16,1,.3,1)', position: 'relative', overflow: 'hidden',
            boxShadow: '0 20px 48px rgba(97,37,123,0.25)',
        }}>
            <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '280px', height: '280px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%' }} />
            <div style={{ position: 'absolute', bottom: '-40px', left: '15%', width: '200px', height: '200px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%' }} />
            <div style={{ position: 'relative' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFD97C', marginBottom: '10px' }}>
                    ¿Quieres organizar un evento?
                </div>
                <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '800', color: '#fff', lineHeight: '1.15', letterSpacing: '-0.02em' }}>
                    Co Marca es tu escenario
                </h3>
                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.88)', marginTop: '10px', maxWidth: '480px', lineHeight: '1.7', textAlign: 'justify' }}>
                    Organizamos o co-organizamos eventos corporativos, lanzamientos, conferencias, talleres y más. Capacidad para hasta 200 personas con todo incluido.
                </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
                <a href="/contacto" style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    background: '#fff', color: '#61257B', borderRadius: '14px', padding: '14px 32px',
                    fontWeight: '800', fontSize: '0.97rem', textDecoration: 'none',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)', transition: 'all 0.22s',
                }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.22)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)'; }}
                >
                    Organizar un evento →
                </a>
                <a href="/contacto" style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '14px', padding: '12px 32px',
                    fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none',
                    border: '1.5px solid rgba(255,255,255,0.3)', transition: 'all 0.22s',
                }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
                >
                    Ver próximos eventos →
                </a>
            </div>
        </div>
    );
}

// ── Main ──
const Eventos: React.FC = () => {
    const { isDark } = useTheme();
    const [activeFilter, setActiveFilter] = useState('todos');
    const [searchQuery, setSearchQuery] = useState('');
    const [rsvpEvent, setRsvpEvent] = useState<EventItem | null>(null);
    const [summaryEvent, setSummaryEvent] = useState<EventItem | null>(null);
    const [eventList, setEventList] = useState<EventItem[]>(events);
    const [loading, setLoading] = useState(false);

    const loadEventos = useCallback(async () => {
        try {
            setLoading(true);
            const apiEvents = await fetchEventosPublicos({ tipo: 'general' });
            if (apiEvents && apiEvents.length > 0) {
                setEventList(apiEvents.map(mapApiEventoToEventItem));
            }
        } catch (err) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEventos();
    }, [loadEventos]);

    const filteredEvents = eventList.filter(e => {
        const matchesFilter = activeFilter === 'todos' || e.status === activeFilter;
        const q = searchQuery.toLowerCase().trim();
        const matchesQuery = !q ||
            e.title.toLowerCase().includes(q) ||
            e.category.toLowerCase().includes(q) ||
            e.description.toLowerCase().includes(q) ||
            e.tags.some(t => t.toLowerCase().includes(q));
        return matchesFilter && matchesQuery;
    });

    const enCurso = filteredEvents.filter(e => e.status === 'en curso');
    const proximos = filteredEvents.filter(e => e.status === 'próximo');
    const pasados = filteredEvents.filter(e => e.status === 'pasado');
    let globalIndex = 0;

    return (
        <div style={{
            fontFamily: "'DM Sans', sans-serif",
            background: isDark ? '#0b0614' : '#f8fafc',
            minHeight: '100vh',
            transition: 'background 0.3s ease',
        }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim { animation: blobFloat 9s ease-in-out infinite; }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(193,52,139,0.4); }
          50% { box-shadow: 0 0 0 5px rgba(193,52,139,0); }
        }
        .fade-up-evt { opacity: 0; transform: translateY(28px); transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1); }
        .fade-up-evt.in { opacity: 1; transform: translateY(0); }
        .fade-up-evt.d1 { transition-delay: 0.1s; }
        .fade-up-evt.d2 { transition-delay: 0.2s; }
        .fade-up-evt.d3 { transition-delay: 0.3s; }
        @media (max-width: 900px) {
          .event-card-inner { flex-direction: column !important; }
          .event-img-side { flex: none !important; width: 100% !important; min-height: 260px !important; }
          .event-content-side { padding: 28px 24px !important; }
        }
      `}</style>

            <HeroSection
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
            />

            <section style={{ padding: '60px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>

                {filteredEvents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: isDark ? '#94a3b8' : '#64748b' }}>
                        <div style={{ marginBottom: '12px' }}><Search size={48} /></div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '6px' }}>
                            No se encontraron eventos
                        </h3>
                        <p style={{ fontSize: '0.9rem' }}>Intenta ajustar tus filtros de búsqueda o seleccionar otra categoría.</p>
                        <button
                            onClick={() => { setActiveFilter('todos'); setSearchQuery(''); }}
                            style={{
                                marginTop: '16px', background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px',
                                fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer',
                            }}
                        >
                            Ver todos los eventos
                        </button>
                    </div>
                ) : (
                    <>
                        {/* EN CURSO */}
                        {enCurso.length > 0 && (
                            <div style={{ marginBottom: '48px' }}>
                                <SectionLabel label="Eventos en curso" color={colors.secondaryLight} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                    {enCurso.map((event) => {
                                        const idx = globalIndex++;
                                        return (
                                            <EventCard
                                                key={event.id}
                                                event={event}
                                                index={idx}
                                                onRegister={setRsvpEvent}
                                                onSummary={setSummaryEvent}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* PRÓXIMOS */}
                        {proximos.length > 0 && (
                            <div style={{ marginBottom: '48px' }}>
                                <SectionLabel label="Próximos eventos" color={colors.secondary} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                    {proximos.map((event) => {
                                        const idx = globalIndex++;
                                        return (
                                            <EventCard
                                                key={event.id}
                                                event={event}
                                                index={idx}
                                                onRegister={setRsvpEvent}
                                                onSummary={setSummaryEvent}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* PASADOS */}
                        {pasados.length > 0 && (
                            <div style={{ marginBottom: '48px' }}>
                                <SectionLabel label="Eventos realizados" color="#94a3b8" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                    {pasados.map((event) => {
                                        const idx = globalIndex++;
                                        return (
                                            <EventCard
                                                key={event.id}
                                                event={event}
                                                index={idx}
                                                onRegister={setRsvpEvent}
                                                onSummary={setSummaryEvent}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <CtaBanner />
            </section>

            {/* Modal de Registro RSVP */}
            {rsvpEvent && (
                <EventRegistrationModal
                    event={rsvpEvent}
                    onClose={() => setRsvpEvent(null)}
                    onSuccess={() => loadEventos()}
                />
            )}

            {/* Modal de Resumen para eventos pasados */}
            {summaryEvent && (
                <EventSummaryModal
                    event={summaryEvent}
                    onClose={() => setSummaryEvent(null)}
                />
            )}
        </div>
    );
};

export default Eventos;